import { useState, useEffect, useCallback } from 'react'
import {
  Users,
  MailCheck,
  MailX,
  Clock,
  Eye,
  RotateCcw,
  XCircle,
  Trash2,
  Download,
  Loader2,
  ShieldAlert,
  CheckCircle2,
  Building2,
  Briefcase,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable, type ColumnDef, useDataTable } from '@/components/ui/data-table'
import { useToast } from '@/components/ui/use-toast'
import { useAuth } from '@/lib/contexts/auth-context'
import { PageHeader, FluentCard, StatCard } from '@/components/layout/page-header'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  betaService,
  type BetaRegistrationDto,
  type BetaRegistrationStatus,
  type BetaStatsDto,
  type TeamSize,
} from '@/lib/api/services/beta'

// Status badge colors and labels
const statusConfig: Record<BetaRegistrationStatus, { label: string; className: string }> = {
  Pending: {
    label: 'Pending',
    className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  },
  VerificationSent: {
    label: 'Verification Sent',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  Verified: {
    label: 'Verified',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  WelcomeSent: {
    label: 'Welcome Sent',
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  Declined: {
    label: 'Declined',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
}

// Team size labels
const teamSizeLabels: Record<TeamSize, string> = {
  Solo: '1-10 people',
  Small: '11-50 people',
  Medium: '51-200 people',
  Large: '201-500 people',
  Enterprise: '500+ people',
  VeryLargeEnterprise: '1000+ people',
}

export default function BetaRegistrations() {
  const { user: currentUser, hasPermission, isLoading: authLoading } = useAuth()
  const canAccessPage = currentUser?.isSuperAdmin || hasPermission('system.admin')

  const [registrations, setRegistrations] = useState<BetaRegistrationDto[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<BetaStatsDto | null>(null)
  const [statsLoading, setStatsLoading] = useState(true)
  const { toast } = useToast()

  // Modal states
  const [selectedRegistration, setSelectedRegistration] = useState<BetaRegistrationDto | null>(null)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [declineModalOpen, setDeclineModalOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [declineReason, setDeclineReason] = useState('')

  // DataTable state
  const {
    page,
    pageSize,
    sortField,
    sortDirection,
    filters,
    search,
    setPage,
    setPageSize,
    setSort,
    setFilters,
    setSearch,
    getODataParams,
  } = useDataTable({
    initialState: {
      sortField: 'createdAt',
      sortDirection: 'desc',
    },
  })

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Show access denied if user doesn't have permission
  if (!canAccessPage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <ShieldAlert className="h-12 w-12 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
        <p className="text-muted-foreground max-w-md mb-6">
          You don't have permission to access Beta Registrations. Please contact your administrator.
        </p>
        <Button variant="outline" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </div>
    )
  }

  // Fetch registrations
  const fetchRegistrations = useCallback(async () => {
    setLoading(true)
    try {
      const params = getODataParams()
      const response = await betaService.getRegistrations({
        ...params,
        count: true,
      })
      setRegistrations(response.value)
      setTotalCount(response['@odata.count'] ?? response.value.length)
    } catch (error) {
      toast({
        title: 'Error fetching registrations',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [getODataParams, toast])

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const data = await betaService.getStats()
      setStats(data)
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    } finally {
      setStatsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRegistrations()
  }, [fetchRegistrations])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  // Actions
  const handleResendVerification = async (registration: BetaRegistrationDto) => {
    setActionLoading(true)
    try {
      await betaService.resendVerification(registration.id)
      toast({
        title: 'Verification email sent',
        description: `Verification email has been resent to ${registration.email}`,
      })
      fetchRegistrations()
    } catch (error) {
      toast({
        title: 'Failed to resend email',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleDecline = async () => {
    if (!selectedRegistration) return
    setActionLoading(true)
    try {
      await betaService.declineRegistration(selectedRegistration.id, declineReason || undefined)
      toast({
        title: 'Registration declined',
        description: `${selectedRegistration.fullName}'s registration has been declined.`,
      })
      setDeclineModalOpen(false)
      setDeclineReason('')
      fetchRegistrations()
      fetchStats()
    } catch (error) {
      toast({
        title: 'Failed to decline registration',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedRegistration) return
    setActionLoading(true)
    try {
      await betaService.deleteRegistration(selectedRegistration.id)
      toast({
        title: 'Registration deleted',
        description: `${selectedRegistration.fullName}'s registration has been deleted.`,
      })
      setDeleteDialogOpen(false)
      fetchRegistrations()
      fetchStats()
    } catch (error) {
      toast({
        title: 'Failed to delete registration',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleExportCSV = async () => {
    try {
      const response = await betaService.getRegistrations({ top: 10000 })
      const data = response.value

      // Build CSV content
      const headers = ['Full Name', 'Email', 'Company', 'Job Title', 'Team Size', 'Industry', 'Status', 'Created At']
      const rows = data.map((r) => [
        r.fullName,
        r.email,
        r.company,
        r.jobTitle,
        r.teamSize ? teamSizeLabels[r.teamSize] : '',
        r.industry || '',
        r.status,
        new Date(r.createdAt).toLocaleDateString(),
      ])

      const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `beta-registrations-${new Date().toISOString().split('T')[0]}.csv`
      link.click()
      URL.revokeObjectURL(url)

      toast({
        title: 'Export complete',
        description: `Exported ${data.length} registrations to CSV.`,
      })
    } catch (error) {
      toast({
        title: 'Export failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  // Table columns
  const columns: ColumnDef<BetaRegistrationDto>[] = [
    {
      id: 'fullName',
      header: 'Name',
      accessor: 'fullName',
      sortable: true,
      filterable: true,
      cell: (row) => (
        <div className="font-medium text-[#1a1a1a] dark:text-white">{row.fullName}</div>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      accessor: 'email',
      sortable: true,
      filterable: true,
      cell: (row) => (
        <span className="text-[#616161] dark:text-[#adadad]">{row.email}</span>
      ),
    },
    {
      id: 'company',
      header: 'Company',
      accessor: 'company',
      sortable: true,
      filterable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-[#888]" />
          <span>{row.company}</span>
        </div>
      ),
    },
    {
      id: 'jobTitle',
      header: 'Job Title',
      accessor: 'jobTitle',
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-[#888]" />
          <span>{row.jobTitle}</span>
        </div>
      ),
    },
    {
      id: 'teamSize',
      header: 'Team Size',
      accessor: (row) => (row.teamSize ? teamSizeLabels[row.teamSize] : '-'),
      sortable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Solo (1-10)', value: 'Solo' },
        { label: 'Small (11-50)', value: 'Small' },
        { label: 'Medium (51-200)', value: 'Medium' },
        { label: 'Large (201-500)', value: 'Large' },
        { label: 'Enterprise (500+)', value: 'Enterprise' },
      ],
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      sortable: true,
      filterType: 'select',
      filterOptions: Object.entries(statusConfig).map(([value, { label }]) => ({
        label,
        value,
      })),
      cell: (row) => {
        const config = statusConfig[row.status]
        return <Badge className={config.className}>{config.label}</Badge>
      },
    },
    {
      id: 'createdAt',
      header: 'Registered',
      accessor: (row) => new Date(row.createdAt).toLocaleDateString(),
      sortable: true,
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: () => null,
      cell: (row) => (
        <TooltipProvider delayDuration={200}>
          <div className="flex items-center gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedRegistration(row)
                    setViewModalOpen(true)
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>View full details</p>
              </TooltipContent>
            </Tooltip>

            {(row.status === 'Pending' || row.status === 'VerificationSent') && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleResendVerification(row)
                    }}
                    disabled={actionLoading}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Resend verification email</p>
                </TooltipContent>
              </Tooltip>
            )}

            {row.status !== 'Declined' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedRegistration(row)
                      setDeclineModalOpen(true)
                    }}
                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Decline registration</p>
                </TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedRegistration(row)
                    setDeleteDialogOpen(true)
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>Delete permanently</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Beta Registrations"
        description="Manage beta program registrations and verification status"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Admin', href: '/dashboard/admin' },
          { label: 'Beta Registrations' },
        ]}
        actions={
          <Button onClick={handleExportCSV} variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Total Registrations"
          value={stats?.totalRegistrations ?? 0}
          icon={Users}
          color="from-violet-400 to-purple-600"
          loading={statsLoading}
        />
        <StatCard
          label="Pending"
          value={stats?.pendingCount ?? 0}
          icon={Clock}
          color="from-slate-400 to-slate-600"
          loading={statsLoading}
        />
        <StatCard
          label="Verified"
          value={stats?.verifiedCount ?? 0}
          icon={MailCheck}
          color="from-blue-400 to-blue-600"
          loading={statsLoading}
        />
        <StatCard
          label="Welcome Sent"
          value={stats?.welcomeSentCount ?? 0}
          icon={CheckCircle2}
          color="from-emerald-400 to-emerald-600"
          loading={statsLoading}
        />
        <StatCard
          label="Declined"
          value={stats?.declinedCount ?? 0}
          icon={MailX}
          color="from-red-400 to-red-600"
          loading={statsLoading}
        />
      </div>

      {/* Data Table */}
      <FluentCard className="overflow-hidden p-5">
        <DataTable
            columns={columns}
            data={registrations}
            totalCount={totalCount}
            loading={loading}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={setSort}
            filters={filters}
            onFilterChange={setFilters}
            searchValue={search}
            onSearchChange={setSearch}
            rowKey="id"
            searchPlaceholder="Search by name, email, company..."
            emptyMessage="No beta registrations found"
          />
      </FluentCard>

      {/* View Details Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Registration Details</DialogTitle>
            <DialogDescription>
              Full details for {selectedRegistration?.fullName}
            </DialogDescription>
          </DialogHeader>
          {selectedRegistration && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Full Name</Label>
                  <p className="font-medium mt-1">{selectedRegistration.fullName}</p>
                </div>
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Email</Label>
                  <p className="font-medium mt-1">{selectedRegistration.email}</p>
                </div>
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Company</Label>
                  <p className="font-medium mt-1">{selectedRegistration.company}</p>
                </div>
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Job Title</Label>
                  <p className="font-medium mt-1">{selectedRegistration.jobTitle}</p>
                </div>
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Industry</Label>
                  <p className="font-medium mt-1">{selectedRegistration.industry || '-'}</p>
                </div>
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Team Size</Label>
                  <p className="font-medium mt-1">
                    {selectedRegistration.teamSize ? teamSizeLabels[selectedRegistration.teamSize] : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Status</Label>
                  <div className="mt-1">
                    <Badge className={statusConfig[selectedRegistration.status].className}>
                      {statusConfig[selectedRegistration.status].label}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Referral Source</Label>
                  <p className="font-medium mt-1">{selectedRegistration.referralSource || '-'}</p>
                </div>
              </div>

              {selectedRegistration.officeAppPriorities && selectedRegistration.officeAppPriorities.length > 0 && (
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Office App Priorities</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedRegistration.officeAppPriorities.map((app, index) => (
                      <Badge key={app} variant="outline" className="text-sm">
                        {index + 1}. {app}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedRegistration.challenges && (
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Challenges</Label>
                  <p className="mt-1 text-[#616161] dark:text-[#adadad] whitespace-pre-wrap">
                    {selectedRegistration.challenges}
                  </p>
                </div>
              )}

              {selectedRegistration.expectations && (
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Expectations</Label>
                  <p className="mt-1 text-[#616161] dark:text-[#adadad] whitespace-pre-wrap">
                    {selectedRegistration.expectations}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-[#e5e5e5] dark:border-[#3d3d3d]">
                <div>
                  <Label className="text-[#888] text-xs uppercase tracking-wide">Registered At</Label>
                  <p className="font-medium mt-1">
                    {new Date(selectedRegistration.createdAt).toLocaleString()}
                  </p>
                </div>
                {selectedRegistration.verifiedAt && (
                  <div>
                    <Label className="text-[#888] text-xs uppercase tracking-wide">Verified At</Label>
                    <p className="font-medium mt-1">
                      {new Date(selectedRegistration.verifiedAt).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Decline Modal */}
      <Dialog open={declineModalOpen} onOpenChange={setDeclineModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Registration</DialogTitle>
            <DialogDescription>
              Are you sure you want to decline {selectedRegistration?.fullName}'s registration?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="declineReason">Reason (optional)</Label>
              <Textarea
                id="declineReason"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Provide a reason for declining..."
                className="mt-2"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeclineModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDecline}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Declining...
                </>
              ) : (
                'Decline Registration'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Registration</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete {selectedRegistration?.fullName}'s registration?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
